import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiDownload, HiExternalLink, HiSparkles } from 'react-icons/hi';
import { FaFilePdf, FaCheck } from 'react-icons/fa';
import './CV.css';

const CV_URL = '/sudip_majkoti_cv.pdf';
const PDF_PAGE_COUNT = 3;
const PDF_PAGE_ASPECT_RATIO = 792 / 612; // US Letter aspect ratio

const CV = () => {
  const wrapperRef = useRef(null);
  const [embedHeight, setEmbedHeight] = useState(null);

  useEffect(() => {
    const updateHeight = () => {
      if (wrapperRef.current) {
        const width = wrapperRef.current.clientWidth;
        if (width > 0) {
          // Calculate full height for all 3 pages at FitH zoom + viewer spacing buffer
          const fullDocHeight = Math.ceil(
            width * PDF_PAGE_ASPECT_RATIO * PDF_PAGE_COUNT + 120
          );
          setEmbedHeight(fullDocHeight);
        }
      }
    };

    updateHeight();

    let observer;
    if (typeof ResizeObserver !== 'undefined' && wrapperRef.current) {
      observer = new ResizeObserver(updateHeight);
      observer.observe(wrapperRef.current);
    }
    window.addEventListener('resize', updateHeight);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <section id="cv" className="cv">
      <div className="container">
        <div className="section-header">
          <span className="section-label">[ 06 // CURRICULUM VITAE ]</span>
          <h2 className="section-title">Resume &amp; CV</h2>
          <p className="section-subtitle">
            Review my complete professional profile, engineering achievements, and technical credentials.
          </p>
        </div>

        {/* Info & Highlights Bar */}
        <motion.div
          className="cv__info-card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <div className="cv__info-top">
            <div className="cv__info-left">
              <div className="cv__status-badge">
                <HiSparkles />
                <span>OFFICIAL CURRICULUM VITAE</span>
              </div>
              <h3 className="cv__info-title">Sudip Majkoti &mdash; AI Engineer</h3>
              <p className="cv__info-desc">
                Comprehensive resume covering AI Agent systems with LangGraph, production RAG pipelines, fine-tuned deep learning models, FastAPI microservices, and Docker containerization.
              </p>
            </div>

            <div className="cv__actions">
              <a
                href={CV_URL}
                download="Sudip_Majkoti_CV.pdf"
                className="btn btn-primary cv__action-btn"
                title="Download PDF directly"
              >
                <HiDownload />
                <span>Download PDF</span>
              </a>
              <a
                href={CV_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline cv__action-btn"
                title="Open PDF in new browser tab"
              >
                <HiExternalLink />
                <span>Open in New Tab</span>
              </a>
            </div>
          </div>

          <ul className="cv__highlights-list">
            <li>
              <FaCheck className="cv__check-icon" />
              <span>Production AI Architectures &amp; Multi-Agent Workflows (LangGraph)</span>
            </li>
            <li>
              <FaCheck className="cv__check-icon" />
              <span>PyTorch, Hugging Face Transformers &amp; Deep Learning</span>
            </li>
            <li>
              <FaCheck className="cv__check-icon" />
              <span>Vector Databases (ChromaDB, Pinecone) &amp; Hybrid Graph Search</span>
            </li>
            <li>
              <FaCheck className="cv__check-icon" />
              <span>Docker &amp; MLOps Deployment Pipelines (FastAPI, Airflow, MLflow)</span>
            </li>
          </ul>
        </motion.div>

        {/* Full-Height Document Viewer */}
        <motion.div
          className="cv__viewer-card"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="cv__viewer-bar">
            <div className="cv__window-dots">
              <span className="cv-dot cv-dot--red" />
              <span className="cv-dot cv-dot--yellow" />
              <span className="cv-dot cv-dot--green" />
            </div>

            <div className="cv__viewer-filename">
              <FaFilePdf className="cv__file-icon" />
              <span>sudip_majkoti_cv.pdf</span>
              <span className="cv__file-tag">[ 100% VECTOR &bull; FULL 3-PAGE PREVIEW ]</span>
            </div>

            <div className="cv__viewer-controls">
              <a
                href={CV_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cv__control-btn"
                title="Open raw PDF in new browser tab"
              >
                <HiExternalLink />
                <span>Open Full</span>
              </a>
              <a
                href={CV_URL}
                download="Sudip_Majkoti_CV.pdf"
                className="cv__control-btn cv__control-btn--highlight"
                title="Download PDF"
              >
                <HiDownload />
                <span>Download</span>
              </a>
            </div>
          </div>

          <div
            ref={wrapperRef}
            className="cv__embed-wrapper"
            style={embedHeight ? { height: `${embedHeight}px` } : undefined}
          >
            <iframe
              src={`${CV_URL}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
              title="Sudip Majkoti Curriculum Vitae Full Preview"
              className="cv__embed-frame"
              scrolling="no"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CV;
